import { useAuth } from "@via/hooks/useAuth";
import { useCallback, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { GenericError } from "@via/components/error.tsx";
import { SkeletonLoader, Spinner } from "@via/components/loading/loading.tsx";
import { ERROR, ROUTES } from "@via/utils/constants";
import DeleteModal from "@via/components/delete-modal/delete-modal";
import UpdateOrganizationForm from "@via/pages/organization/update-organization-form.tsx";
import InviteUrlForm from "@via/pages/organization/settings/invite-url-form.tsx";
import {
  useCancelOrKeepSubscription,
  useCheckSubscriptionHealth,
  useCreateSetupIntent,
} from "@via/pages/billing/billing.api";
import { StripeProvider } from "@via/context/stripe-context.tsx";
import UpdateBillingInfoForm from "@via/pages/billing/update-billing-info-form.tsx";
import { useRegisterCortexContext } from "@via/hooks/useRegisterCortexContext.tsx";
import { useConfigurations } from "@via/api/configuration";
import ToggleButton from "@via/components/toggle-button";
import { useUpdateOrganizationSettings } from "@via/pages/organization/organization.api";
import ImpersonateServiceAccountModal from "@via/pages/organization/settings/impersonate-service-account-modal.tsx";
import AssumeRoleCredentialModal from "@via/pages/organization/settings/assume-role-credential-modal.tsx";
import {
  canCheckSubscriptionHealth,
  canManageOrganization,
  organizationMembershipFor,
} from "@via/hooks/organization-access.ts";

const Settings = () => {
  const navigate = useNavigate();
  const { organizationId = "" } = useParams();
  const { user } = useAuth();
  const { data: configurations } = useConfigurations();
  const [clientSecret, setClientSecret] = useState<string | null>(null);
  const [isOrganizationUpdateDisabled, setIsOrganizationUpdateDisabled] =
    useState<boolean>(true);
  const [isBillingInfoDisabled, setIsBillingInfoDisabled] =
    useState<boolean>(true);
  const [isSubscriptionToBeCanceled, setSubscriptionToBeCanceled] =
    useState<boolean>(false);
  const [isImpersonateModalOpen, setIsImpersonateModalOpen] =
    useState<boolean>(false);
  const [isAssumeRoleModalOpen, setIsAssumeRoleModalOpen] =
    useState<boolean>(false);

  const onboardingStep = user?.onboardingStep;

  // Always the membership for the organization in the route. userOrganizations[0] is whichever
  // organization sorts first and showed (and edited) THAT organization's settings here.
  const membership = organizationMembershipFor(user, organizationId);

  const { mutate: updateSettings, isPending: isUpdateSettingsLoading } =
    useUpdateOrganizationSettings();

  const allowDownloadFolder =
    membership?.settings?.allowDownloadFolder !== false;

  const datasetDownloadNotification =
    membership?.settings?.datasetDownloadNotification !== false;

  const requireRoleBasedAwsAuth =
    membership?.settings?.requireRoleBasedAwsAuth === true;

  const handleToggleDownloadFolder = (value: boolean) => {
    updateSettings({
      requestParams: { id: organizationId },
      requestBody: { settings: { allowDownloadFolder: value } },
    });
  };

  const handleToggleDatasetDownloadNotification = (value: boolean) => {
    updateSettings({
      requestParams: { id: organizationId },
      requestBody: { settings: { datasetDownloadNotification: value } },
    });
  };

  const handleToggleRequireRoleBasedAwsAuth = (value: boolean) => {
    updateSettings({
      requestParams: { id: organizationId },
      requestBody: { settings: { requireRoleBasedAwsAuth: value } },
    });
  };

  const {
    mutate: createSetupIntent,
    isPending: isCreateSetupIntentLoading,
    isError: isCreateSetupIntentError,
  } = useCreateSetupIntent();

  const {
    mutateAsync: cancelOrKeepSubscription,
    isPending: isCancelOrKeepSubscriptionLoading,
  } = useCancelOrKeepSubscription();

  const {
    data: checkSubscriptionHealth,
    isLoading: isCheckSubscriptionHealthLoading,
    isError: isCheckSubscriptionHealthError,
  } = useCheckSubscriptionHealth({ enabled: canCheckSubscriptionHealth(user) });

  useEffect(() => {
    if (configurations?.ENABLE_STRIPE) {
      createSetupIntent(undefined, {
        onSuccess: data => setClientSecret(data.clientSecret || null),
      });
    }
  }, [createSetupIntent, configurations?.ENABLE_STRIPE]);

  useEffect(() => {
    // undefined = the user has not loaded yet; redirecting on it bounced every first visit.
    const isAdmin = canManageOrganization(user, organizationId);
    if (isAdmin === false) {
      navigate(`/organization/${organizationId}/activity`);
      return;
    }
  }, [user, organizationId]);

  const handleOrganizationUpdateSuccess = () => {
    setIsOrganizationUpdateDisabled(true);
  };

  const getSettingsContext = useCallback(() => {
    const organization = organizationMembershipFor(user, organizationId);

    return {
      organizationId,
      organization: {
        name: organization?.name,
        description: organization?.description,
        role: organization?.role?.name,
      },
      subscription: checkSubscriptionHealth
        ? {
            status: checkSubscriptionHealth.activeSubscriptionStatus,
            cancelAtPeriodEnd: checkSubscriptionHealth.cancelAtPeriodEnd,
            cancelAt: checkSubscriptionHealth.cancelAt
              ? new Date(checkSubscriptionHealth.cancelAt * 1000).toISOString()
              : null,
          }
        : null,
      billing: {
        hasClientSecret: !!clientSecret,
        email: user?.email,
      },
    };
  }, [organizationId, user, checkSubscriptionHealth, clientSecret]);

  useRegisterCortexContext({
    routePattern: ROUTES.ORGANIZATION_SETTINGS,
    contextName: "Organization Settings",
    getContext: getSettingsContext,
    systemPrompt:
      "You are analyzing organization settings page. Help answer questions about organization information, subscription status, billing details, user management, and account deactivation.",
    enabled: !!organizationId && !!membership,
  });

  return (
    <>
      <div className="flex flex-row gap-10 flex-1 pb-24 overflow-y-auto">
        <div className="flex-1">
          <h2 className="via-h2">Organization Info</h2>
          <UpdateOrganizationForm
            organizationId={organizationId}
            onUpdateSuccess={handleOrganizationUpdateSuccess}
            onboardingStep={onboardingStep}
            isEditMode
            onEdit={() => {
              setIsOrganizationUpdateDisabled(prev => !prev);
            }}
            isDisabled={isOrganizationUpdateDisabled}
            defaultValues={{
              name: membership?.name || "",
              description: membership?.description || "",
            }}
          />
          <div>
            <h2 className="via-h2 mt-8">Organization Invite Link</h2>
            <InviteUrlForm
              organizationId={organizationId}
              initialSlug={membership?.slug ?? null}
            />
          </div>
          <div>
            <h2 className="via-h2 mt-8">Dataset Settings</h2>
            <div className="mt-4 flex items-center gap-4 rounded-lg border border-gray-200 bg-white px-4 py-3">
              <div className="min-w-0 flex-1">
                <p className="text-sm font-medium text-gray-900">
                  Enable &quot;Add Folder for Download Only&quot;
                </p>
                <p className="text-sm text-gray-500">
                  Applies to all users in this organization
                </p>
              </div>
              <ToggleButton
                isOn={allowDownloadFolder}
                onToggle={handleToggleDownloadFolder}
                isLoading={isUpdateSettingsLoading}
              />
            </div>
            <div className="mt-3 flex items-center gap-4 rounded-lg border border-gray-200 bg-white px-4 py-3">
              <div className="min-w-0 flex-1">
                <p className="text-sm font-medium text-gray-900">
                  Email admins on dataset download
                </p>
                <p className="text-sm text-gray-500">
                  Notify organization admins via email when a user downloads a
                  dataset
                </p>
              </div>
              <ToggleButton
                isOn={datasetDownloadNotification}
                onToggle={handleToggleDatasetDownloadNotification}
                isLoading={isUpdateSettingsLoading}
              />
            </div>
          </div>
          <div>
            <h2 className="via-h2 mt-8">Run Authentication</h2>
            <div className="mt-4 flex items-center gap-4 rounded-lg border border-gray-200 bg-white px-4 py-3">
              <div className="min-w-0 flex-1">
                <p className="text-sm font-medium text-gray-900">
                  Require role-based AWS authentication for runs
                </p>
                <p className="text-sm text-gray-500">
                  When on, runs cannot use static AWS keys. Users must choose an
                  assume-role credential or an attached instance role.
                </p>
              </div>
              <ToggleButton
                isOn={requireRoleBasedAwsAuth}
                onToggle={handleToggleRequireRoleBasedAwsAuth}
                isLoading={isUpdateSettingsLoading}
              />
            </div>
          </div>
          <div className="mt-8">
            <h2 className="via-h2">Impersonation Service Accounts</h2>
            <div className="mt-4 rounded-lg border border-gray-200 bg-white px-4 py-4">
              <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                <div className="min-w-0">
                  <p className="text-sm font-medium text-gray-900">
                    Configure impersonation service accounts
                  </p>
                  <p className="text-sm text-gray-500">
                    Add organization-wide GCP service accounts for dataset
                    operations.
                  </p>
                </div>
                <button
                  type="button"
                  onClick={() => setIsImpersonateModalOpen(true)}
                  className="inline-flex justify-center rounded-md bg-indigo-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-indigo-500">
                  Configure
                </button>
              </div>
            </div>
          </div>
          <div className="mt-8">
            <h2 className="via-h2">Assume Role Credentials</h2>
            <div className="mt-4 rounded-lg border border-gray-200 bg-white px-4 py-4">
              <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                <div className="min-w-0">
                  <p className="text-sm font-medium text-gray-900">
                    Configure AWS assume-role credentials
                  </p>
                  <p className="text-sm text-gray-500">
                    Add organization-wide AWS IAM role ARNs runs can assume.
                  </p>
                </div>
                <button
                  type="button"
                  onClick={() => setIsAssumeRoleModalOpen(true)}
                  className="inline-flex justify-center rounded-md bg-indigo-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-indigo-500">
                  Configure Assume-Role Credentials
                </button>
              </div>
            </div>
          </div>
        </div>
        <div className="flex-1">
          {configurations?.ENABLE_STRIPE && (
            <>
              <h2 className="via-h2">Subscription Info</h2>
              {isCheckSubscriptionHealthLoading ? (
                <div className="grid grid-cols-2 gap-6 py-6 sm:grid-cols-2 lg:grid-cols-2">
                  <SkeletonLoader count={2} />
                </div>
              ) : isCheckSubscriptionHealthError || !checkSubscriptionHealth ? (
                // A failed or skipped check is no billing gate (#980), so say so plainly
                // instead of a skeleton that never resolves.
                <p className="mt-4 mb-4 text-sm text-gray-500">
                  Subscription status is not available.
                </p>
              ) : (
                <div className="mb-4 text-sm text-gray-700 flex flex-col gap-4">
                  <div className="flex items-center gap-4">
                    <span>
                      Subscription Status:{" "}
                      <strong className="capitalize text-green-600">
                        {checkSubscriptionHealth.activeSubscriptionStatus}
                      </strong>
                      {checkSubscriptionHealth.cancelAtPeriodEnd &&
                        checkSubscriptionHealth.cancelAt && (
                          <span className="ml-2 text-orange-500">
                            {`(Cancels at${" "}${new Date(
                              checkSubscriptionHealth.cancelAt * 1000
                            ).toLocaleDateString()})`}
                          </span>
                        )}
                    </span>

                    {/* Cancel button (only show if active or trialing and not already canceling) */}
                    {(checkSubscriptionHealth.activeSubscriptionStatus ===
                      "active" ||
                      checkSubscriptionHealth.activeSubscriptionStatus ===
                        "trialing") &&
                      !checkSubscriptionHealth.cancelAtPeriodEnd && (
                        <button
                          onClick={() => setSubscriptionToBeCanceled(true)}
                          className="ml-auto rounded-md bg-red-600 px-3 py-1.5 text-sm font-semibold text-white shadow-sm hover:bg-red-500 disabled:opacity-50">
                          Cancel Subscription
                        </button>
                      )}

                    {/* Undo Cancellation */}
                    {(checkSubscriptionHealth.activeSubscriptionStatus ===
                      "active" ||
                      checkSubscriptionHealth.activeSubscriptionStatus ===
                        "trialing") &&
                      checkSubscriptionHealth.cancelAtPeriodEnd && (
                        <button
                          onClick={() =>
                            cancelOrKeepSubscription({ cancel: false })
                          }
                          disabled={isCancelOrKeepSubscriptionLoading}
                          className="ml-auto whitespace-nowrap inline-flex justify-center rounded-md bg-indigo-600 px-3 py-1 text-sm font-semibold text-white shadow-sm hover:bg-indigo-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600 disabled:opacity-50">
                          <Spinner loading={isCancelOrKeepSubscriptionLoading}>
                            Keep Subscription Active
                          </Spinner>
                        </button>
                      )}
                  </div>
                </div>
              )}

              <h2 className="via-h2 mt-8">Billing Info</h2>

              {isCreateSetupIntentLoading || !clientSecret ? (
                <div className="grid grid-cols-2 gap-6 py-6 sm:grid-cols-2 lg:grid-cols-2">
                  <SkeletonLoader count={4} />
                </div>
              ) : isCreateSetupIntentError ? (
                <div className="via-generic-error lg:py-12">
                  <GenericError message={ERROR} />
                </div>
              ) : (
                <StripeProvider clientSecret={clientSecret}>
                  <UpdateBillingInfoForm
                    clientSecret={clientSecret}
                    email={user.email}
                    onEdit={() => {
                      if (isBillingInfoDisabled) {
                        // If entering edit mode, generate a new setupIntent
                        createSetupIntent(undefined, {
                          onSuccess: data => {
                            if (data.clientSecret) {
                              setClientSecret(data.clientSecret);
                              setIsBillingInfoDisabled(false);
                            }
                          },
                        });
                      } else {
                        setIsBillingInfoDisabled(true);
                      }
                    }}
                    isDisabled={isBillingInfoDisabled}
                  />
                </StripeProvider>
              )}
            </>
          )}
        </div>
      </div>
      {canManageOrganization(user, organizationId) === true && (
        <>
          <ImpersonateServiceAccountModal
            organizationId={organizationId}
            open={isImpersonateModalOpen}
            setOpen={setIsImpersonateModalOpen}
          />
          <AssumeRoleCredentialModal
            organizationId={organizationId}
            open={isAssumeRoleModalOpen}
            setOpen={setIsAssumeRoleModalOpen}
          />
          {isSubscriptionToBeCanceled && (
            <DeleteModal
              item={isSubscriptionToBeCanceled}
              setItem={setSubscriptionToBeCanceled}
              deleteFunction={async () => {
                await cancelOrKeepSubscription({ cancel: true });
              }}
              isLoading={isCancelOrKeepSubscriptionLoading}
              title="Cancel Subscription"
              message={() => (
                <div className="space-y-4 text-sm text-gray-700">
                  <p>
                    Are you sure you want to cancel your subscription? This will
                    prevent any further charges, but you will retain access
                    until the end of your current billing period.
                  </p>
                  <p className="text-red-600 font-medium">
                    Note: Your organization will remain active, but you will
                    lose access to premium features.
                  </p>
                </div>
              )}
              confirmText={"Cancel Subscription"}
            />
          )}
        </>
      )}
    </>
  );
};

export default Settings;
